// The backend's check of a booklet's printed phrases (exams/booklet/labels.py: clean_labels and fillable), so that
// the page refuses what the server would, and nothing else.

// The phrases the layout fills in (with Python's str.format) and the placeholders each keeps. closing_en ends a
// subject whose title is English (Latin letters), in either stream.
export const PLACEHOLDERS = { variant: ["code"], closing: ["title"], closing_en: ["title"], passage_note: ["first", "last"] };
export const LABEL_LIMIT = 200;

/**
 * The fields of `value` as Python's str.format reads it: [{name, plain}], `plain` for a bare {name} (no conversion
 * such as {code!r}, no format spec such as {code:>5}). null when a brace stands alone: a brace printed as it is
 * is doubled ({{ or }}).
 */
export function formatFields(value) {
  const fields = [];
  const end = value.length;
  let i = 0;
  while (i < end) {
    const brace = value[i++];
    if (brace !== "{" && brace !== "}") continue;
    if (value[i] === brace) {
      i += 1;
      continue;
    }
    if (brace === "}") return null;
    // The name runs to "}", ":" or "!"; an index ("[…]") in it is passed over whole.
    const start = i;
    let stop = "";
    while (i < end && !stop) {
      const c = value[i++];
      if (c === "{") return null;
      if (c === "[") {
        while (i < end && value[i] !== "]") i += 1;
      } else if (c === "}" || c === ":" || c === "!") stop = c;
    }
    if (!stop) return null;
    const name = value.slice(start, i - 1);
    if (stop === "}") {
      fields.push({ name, plain: true });
      continue;
    }
    let conversion = false;
    if (stop === "!") {
      if (i >= end) return null;
      i += 1;
      conversion = true;
      if (i < end) {
        const c = value[i++];
        if (c === "}") {
          fields.push({ name, plain: false });
          continue;
        }
        if (c !== ":") return null;
      }
    }
    // The format spec runs to its own closing brace (braces nested in it counted).
    const specStart = i;
    let depth = 1;
    while (i < end && depth) {
      const c = value[i++];
      if (c === "{") depth += 1;
      else if (c === "}") depth -= 1;
    }
    if (depth) return null;
    // An empty spec ({code:}) changes nothing: the server takes it.
    fields.push({ name, plain: !conversion && i - 1 === specStart });
  }
  return fields;
}

/**
 * What the server would refuse in phrase `key` written as `value` (trimmed), or null:
 * - "empty", "too_long" (more than LABEL_LIMIT characters, counted as Python counts them);
 * - "braces": a brace standing alone, a field other than the phrase's placeholders written plainly (another
 *   name, a number, an index, a conversion, a format spec), or a placeholder in doubled braces, which prints as it is;
 * - "missing": a placeholder the phrase lost (`missing`).
 * The phrases the layout does not fill in take any text, braces too.
 */
export function labelProblem(key, value) {
  if (!value) return { problem: "empty" };
  if (Array.from(value).length > LABEL_LIMIT) return { problem: "too_long" };
  const names = PLACEHOLDERS[key];
  if (!names) return null;
  const fields = formatFields(value);
  const missing = names.filter((name) => !fields?.some((field) => field.name === name));
  if (!fields || fields.some((field) => !field.plain || !names.includes(field.name)) || missing.some((name) => value.includes(`{${name}}`))) {
    return { problem: "braces", placeholders: names };
  }
  if (missing.length) return { problem: "missing", placeholders: names, missing };
  return null;
}
